import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicBookingService } from '../../services/api';
import { formatApptTime, localDateKey } from '../../utils/dateFormatters';
import { priceLabel } from '../../utils/pricing';
import { sendVisit } from '../../utils/visitBeacon';
import './Booking.css';

const LOCATION_LABEL = { in_person: 'In person', phone: 'Phone call', video: 'Video call', client_location: 'At your place' };
const STEPS = ['Job', 'Date', 'Time', 'Details'];

function dateLabel(key) {
  return new Date(key + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Service-first booking flow: pick what you need done, then a day and time.
// Each service belongs to a staff member; the slot engine resolves that
// server-side, so the public page stays single-brand.
export default function BookingWizard() {
  const { staffSlug: slugParam } = useParams();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [service, setService] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState({
    name: '', email: '', phone: '', notes: '',
    address_street: '', address_suburb: '', address_state: 'QLD', address_postcode: ''
  });
  const [photos, setPhotos] = useState([]); // File objects, max 5
  const [photoNote, setPhotoNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // booking result

  // Count the visit (GID-Session-022) — the server folds a reload into the same view.
  useEffect(() => { sendVisit({ site: 'book' }); }, []);

  // Load job types — all of them, or one person's on a /book/:staffSlug deep link.
  useEffect(() => {
    setLoadingServices(true);
    const load = slugParam
      ? publicBookingService.getStaffServices(slugParam)
      : publicBookingService.listServices();
    load.then((r) => {
      if (r.success) setServices(r.services || []);
      else setError('Booking is not available right now. Please try again later.');
      setLoadingServices(false);
    });
  }, [slugParam]);

  async function selectService(svc) {
    setError('');
    setService(svc);
    setSlot(null); setDate(null);
    setStep(1);
    setLoadingSlots(true);
    const r = await publicBookingService.getSlots(svc.id);
    setSlots(r.success ? (r.slots || []) : []);
    setLoadingSlots(false);
  }

  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) { const k = localDateKey(s.start); (map[k] ||= []).push(s); }
    return map;
  }, [slots]);
  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  const needsAddress = service?.location_mode === 'client_location';

  async function confirm() {
    setError('');
    if (!details.name.trim()) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) { setError('Please enter a valid email.'); return; }
    if (!details.phone.trim()) { setError('Please enter your phone number.'); return; }
    if (needsAddress && (!details.address_street.trim() || !details.address_suburb.trim())) {
      setError('Please enter the property address (street and suburb).'); return;
    }
    setSubmitting(true);
    const r = await publicBookingService.createBooking({
      serviceId: service.id, startsAt: slot.start,
      name: details.name.trim(), email: details.email.trim(), phone: details.phone.trim(), notes: details.notes.trim(),
      address_street: details.address_street.trim(), address_suburb: details.address_suburb.trim(),
      address_state: details.address_state.trim(), address_postcode: details.address_postcode.trim()
    });
    if (!r.success) {
      setSubmitting(false);
      setError(r.error || 'Sorry, that time is no longer available. Please pick another.');
      return;
    }
    // Booking is locked in — photos are best-effort on top.
    if (photos.length) {
      const up = await publicBookingService.uploadBookingImages(r.booking.manage_token, photos);
      setPhotoNote(up.success
        ? `We got your ${photos.length} photo${photos.length === 1 ? '' : 's'} too.`
        : 'Your booking is confirmed, but the photos did not upload — you can reply to the confirmation email with them instead.');
    }
    setSubmitting(false);
    setDone(r.booking);
    sendVisit({ site: 'book', event: 'booked' });
  }

  function addPhotos(fileList) {
    const incoming = [...fileList].filter((f) => f.type.startsWith('image/'));
    setPhotos((prev) => [...prev, ...incoming].slice(0, 5));
  }
  function removePhoto(idx) {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  }

  function back() { setError(''); setStep((s) => Math.max(0, s - 1)); }

  // ---- Done screen ----
  if (done) {
    return (
      <div className="booking-card booking-done">
        <div className="done-check">✓</div>
        <h1>You're booked in!</h1>
        <p className="text-muted">{service.name}</p>
        <p className="done-when">{new Date(done.starts_at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', hour12: true })}</p>
        {needsAddress && details.address_street && (
          <p className="text-muted">{[details.address_street, details.address_suburb, details.address_state, details.address_postcode].filter(Boolean).join(', ')}</p>
        )}
        <p className="text-muted">A confirmation has been sent to {details.email}.</p>
        {photoNote && <p className="text-muted">{photoNote}</p>}
        <div className="done-actions">
          <Link className="btn btn-outline" to={`/booking/${done.manage_token}`}>Manage booking</Link>
          <Link className="btn btn-primary" to="/book" onClick={() => window.location.reload()}>Book another</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-wizard">
      <div className="stepper">
        {STEPS.map((label, i) => (
          <div key={label} className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span>{i + 1}</span><label>{label}</label>
          </div>
        ))}
      </div>

      {error && <div className="booking-error">{error}</div>}

      <div className="booking-card">
        {step === 0 && (
          <>
            <h1>What do you need done?</h1>
            <Link className="qr-nudge" to="/get-a-quote">
              <strong>Just want a price first?</strong> Get a free Smart Quote: send us your address and we'll price it from the satellite map, with no visit and no time to pick. →
            </Link>
            {loadingServices ? <div className="loading-spinner"></div> : (
              <div className="choice-list">
                {services.length === 0 && <p className="text-muted">No job types available right now. Please check back later.</p>}
                {services.map((svc) => (
                  <div key={svc.id} className="choice-wrap">
                    <button className="choice" onClick={() => selectService(svc)}>
                      <span className="choice-body">
                        <strong>{svc.name}</strong>
                        {svc.description && <span className="text-muted">{svc.description}</span>}
                        <span className="choice-meta">
                          {svc.duration_minutes ? <span className="chip">{svc.duration_minutes} min</span> : null}
                          <span className="chip">{LOCATION_LABEL[svc.location_mode] || svc.location_mode}</span>
                          {priceLabel(svc) ? <span className="chip chip-price">{priceLabel(svc)}</span> : null}
                        </span>
                      </span>
                    </button>
                    {svc.info_url && (
                      <a className="choice-info-link" href={svc.info_url} target="_blank" rel="noopener noreferrer">
                        What's included + before &amp; afters ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <h1>Pick a day</h1>
            <p className="text-muted booking-sub">{service?.name}{service?.duration_minutes ? ` · ${service.duration_minutes} min` : ''}</p>
            {loadingSlots ? <div className="loading-spinner"></div>
              : availableDates.length === 0 ? <p className="text-muted">No availability in the booking window. Please check back later.</p>
              : (
                <div className="date-grid">
                  {availableDates.map((k) => (
                    <button key={k} className={`date-chip ${date === k ? 'active' : ''}`} onClick={() => { setDate(k); setStep(2); }}>
                      {dateLabel(k)}
                    </button>
                  ))}
                </div>
              )}
          </>
        )}

        {step === 2 && (
          <>
            <h1>Pick a time</h1>
            <p className="text-muted booking-sub">{date && dateLabel(date)}</p>
            <div className="time-grid">
              {(slotsByDate[date] || []).map((s) => (
                <button key={s.start} className={`time-chip ${slot?.start === s.start ? 'active' : ''}`} onClick={() => { setSlot(s); setStep(3); }}>
                  {formatApptTime(s.start)}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h1>Your details</h1>
            <p className="text-muted booking-sub">{service?.name} · {date && dateLabel(date)} at {slot && formatApptTime(slot.start)}</p>
            <div className="form-group"><label htmlFor="name">Name *</label>
              <input id="name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} autoFocus /></div>
            <div className="form-group"><label htmlFor="email">Email *</label>
              <input id="email" type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} /></div>
            <div className="form-group"><label htmlFor="phone">Phone *</label>
              <input id="phone" type="tel" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} /></div>
            {needsAddress && (
              <>
                <div className="form-group"><label htmlFor="address_street">Property address *</label>
                  <input id="address_street" placeholder="Street address" autoComplete="street-address"
                    value={details.address_street} onChange={(e) => setDetails({ ...details, address_street: e.target.value })} /></div>
                <div className="form-row">
                  <div className="form-group"><label htmlFor="address_suburb">Suburb *</label>
                    <input id="address_suburb" value={details.address_suburb} onChange={(e) => setDetails({ ...details, address_suburb: e.target.value })} /></div>
                  <div className="form-group"><label htmlFor="address_state">State</label>
                    <input id="address_state" value={details.address_state} onChange={(e) => setDetails({ ...details, address_state: e.target.value })} /></div>
                  <div className="form-group"><label htmlFor="address_postcode">Postcode</label>
                    <input id="address_postcode" inputMode="numeric" value={details.address_postcode} onChange={(e) => setDetails({ ...details, address_postcode: e.target.value })} /></div>
                </div>
              </>
            )}
            <div className="form-group"><label htmlFor="notes">Anything we should know?</label>
              <textarea id="notes" rows="3" placeholder={needsAddress ? 'e.g. two-storey house, dogs in the yard, gate code…' : ''}
                value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} /></div>
            <div className="form-group">
              <label htmlFor="photos">Photos of the job (optional, up to 5)</label>
              <p className="photo-hint">A couple of quick phone shots help us quote faster.</p>
              <input id="photos" type="file" accept="image/*" multiple
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
              {photos.length > 0 && (
                <div className="photo-thumbs">
                  {photos.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="photo-thumb">
                      <img src={URL.createObjectURL(f)} alt={f.name} />
                      <button type="button" className="photo-remove" aria-label={`Remove ${f.name}`} onClick={() => removePhoto(i)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="booking-privacy-note">
              We only use your details to arrange and manage your job — see our{' '}
              <a href="https://getitdone.getit-media.com.au/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
            </p>
          </>
        )}
      </div>

      <div className="booking-actions">
        {step > 0 && <button className="btn btn-outline" onClick={back}>Back</button>}
        {step === 3 && (
          <button className="btn btn-primary booking-confirm" onClick={confirm} disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
          </button>
        )}
      </div>
    </div>
  );
}
